/**
 * An RFC's row in the account's Rules list (§5.27, Claude Design R10) —
 * number, title, status, its draft mark, how many statements and the summary.
 * The RFC chosen is the sheet (`rules-sheet.tsx`).
 */
import type React from 'react';
import type { RuleRfc } from '@airlock/contracts/rules/document';
import { color, font } from '../../../theme/tokens';

type Mark = 'new' | 'edited' | 'deleted' | null;

export function RulesListRow({
  rfc,
  origin,
  mark,
  on,
  count,
  onSelect
}: {
  rfc: RuleRfc;
  origin: string;
  mark: Mark;
  on: boolean;
  /** How many statements the search and filters leave, while they are set. */
  count: number | null;
  onSelect: () => void;
}): React.JSX.Element {
  const statements = count ?? rfc.statements.length;
  return (
    <button
      type="button"
      aria-current={on ? 'true' : undefined}
      onClick={onSelect}
      style={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: 3,
        padding: '9px 12px',
        border: 0,
        borderBottom: `1px solid ${color.hairSoft}`,
        background: on ? color.noteTealBg : color.surface,
        boxShadow: on ? `inset 2px 0 0 ${color.accent}` : 'none',
        textAlign: 'left',
        font: 'inherit',
        color: 'inherit',
        cursor: 'pointer',
        opacity: mark === 'deleted' ? 0.6 : 1
      }}
    >
      <span style={{ display: 'flex', alignItems: 'baseline', gap: 6, minWidth: 0 }}>
        <span style={{ flex: 'none', fontFamily: font.mono, fontSize: 11.5, color: color.inkFaint }}>
          {origin}:{rfc.rfc}
        </span>
        <span
          title={rfc.title}
          style={{
            flex: 1,
            minWidth: 0,
            fontSize: 13.5,
            fontWeight: 600,
            color: color.ink,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            textDecoration: mark === 'deleted' ? 'line-through' : 'none'
          }}
        >
          {rfc.title}
        </span>
      </span>
      <span style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 5 }}>
        <Tag tone={rfc.status === 'enforced' ? 'accent' : 'plain'}>{rfc.status}</Tag>
        {mark && <Tag tone="warn">{mark === 'new' ? 'new · draft' : mark === 'edited' ? 'edited · draft' : 'deleted · draft'}</Tag>}
        <span style={{ fontSize: 12, color: color.inkFaint }}>
          {statements} statement{statements === 1 ? '' : 's'}
          {count !== null ? ' match' : ''}
        </span>
      </span>
      <span style={{ fontSize: 12, color: color.inkSubtle, lineHeight: 1.45, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
        {rfc.summary}
      </span>
    </button>
  );
}

function Tag({ tone, mono, children }: { tone: 'accent' | 'warn' | 'bad' | 'plain'; mono?: boolean; children: React.ReactNode }): React.JSX.Element {
  const [background, ink] =
    tone === 'accent' ? [color.okBg, color.okText] : tone === 'warn' ? [color.warnBg, color.warnText] : tone === 'bad' ? [color.badBg, color.badText] : [color.surfaceAlt, color.offText];
  return (
    <span style={{ padding: '1px 7px', borderRadius: 999, background, color: ink, fontSize: 11, fontFamily: mono ? font.mono : 'inherit', whiteSpace: 'nowrap' }}>{children}</span>
  );
}
